import { API_BASE_URL } from '../config/api';

// Get all MCP servers available to the current user
export const fetchMCPServers = async () => {
  const response = await fetch(`${API_BASE_URL}/mcp`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch MCP servers');
  }

  return response.json();
};

// Get all MCP servers (admin only)
export const fetchAllMCPServers = async () => {
  const response = await fetch(`${API_BASE_URL}/mcp/admin/all`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch all MCP servers');
  }

  return response.json();
};

// Get a single MCP server by ID
export const fetchMCPServer = async (id) => {
  const response = await fetch(`${API_BASE_URL}/mcp/${id}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch MCP server');
  }

  return response.json();
};

// Create a new MCP server (admin only)
export const createMCPServer = async (mcpServerData) => {
  const response = await fetch(`${API_BASE_URL}/mcp`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mcpServerData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create MCP server');
  }

  return response.json();
};

// Update an existing MCP server (admin only)
export const updateMCPServer = async (id, mcpServerData) => {
  const response = await fetch(`${API_BASE_URL}/mcp/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mcpServerData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update MCP server');
  }

  return response.json();
};

// Delete an MCP server (admin only)
export const deleteMCPServer = async (id) => {
  const response = await fetch(`${API_BASE_URL}/mcp/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete MCP server');
  }

  return response.json();
};

// MCP Server Templates
export const MCP_TEMPLATES = {
  'google-ads': {
    name: 'Google Ads MCP',
    type: 'google-ads',
    description: 'Interact with Google Ads API for campaign management and analysis',
    command: 'pipx',
    args: [
      'run',
      '--spec',
      'git+https://github.com/googleads/google-ads-mcp.git',
      'google-ads-mcp'
    ],
    env: {
      GOOGLE_OAUTH_CLIENT_JSON: '',  // For OAuth2 web flow - paste your client credentials
      GOOGLE_OAUTH_REFRESH_TOKEN: '',  // User's refresh token after OAuth consent
      GOOGLE_PROJECT_ID: '',
      GOOGLE_ADS_DEVELOPER_TOKEN: '',
      GOOGLE_ADS_LOGIN_CUSTOMER_ID: ''
    },
    icon: 'Target',
    helpText: `Configuration Options:

Option 1 - OAuth2 Web Client (Your credentials):
1. GOOGLE_OAUTH_CLIENT_JSON: Paste your OAuth client JSON (the "web" credentials you have)
2. GOOGLE_OAUTH_REFRESH_TOKEN: Will be generated after user authorizes access
3. GOOGLE_PROJECT_ID: "gbp-category-list" (from your credentials)
4. GOOGLE_ADS_DEVELOPER_TOKEN: Get from Google Ads → Tools → API Center
5. GOOGLE_ADS_LOGIN_CUSTOMER_ID: (Optional) Manager account customer ID

Note: Users will need to authorize access via OAuth flow first time`
  },
  'google-ads-keyword-planner': {
    name: 'Google Ads Keyword Planner MCP',
    type: 'google-ads-keyword-planner',
    description: 'Access Google Ads Keyword Planner API for keyword research, search volume, and competition data',
    command: 'python',
    args: [
      '/app/mcp-servers/google-ads-keyword-planner/server.py'
    ],
    env: {
      GOOGLE_OAUTH_CLIENT_JSON: '',  // OAuth2 client credentials
      GOOGLE_OAUTH_REFRESH_TOKEN: '',  // User's refresh token after OAuth consent
      GOOGLE_ADS_DEVELOPER_TOKEN: '',  // Google Ads Developer Token
      GOOGLE_ADS_LOGIN_CUSTOMER_ID: ''  // (Optional) Manager account customer ID
    },
    icon: 'Search',
    helpText: `Google Ads Keyword Planner Configuration:

Required Credentials:
1. GOOGLE_OAUTH_CLIENT_JSON: OAuth client JSON from Google Cloud Console
   - Create at: https://console.cloud.google.com/apis/credentials
   - Type: OAuth 2.0 Client ID (Web application)
   - Required scopes: https://www.googleapis.com/auth/adwords

2. GOOGLE_OAUTH_REFRESH_TOKEN: Generated after user authorization
   - Will be created automatically during OAuth flow

3. GOOGLE_ADS_DEVELOPER_TOKEN: From Google Ads API Center
   - Access: Google Ads → Tools & Settings → API Center
   - Must have "Basic" or "Standard" access level

4. GOOGLE_ADS_LOGIN_CUSTOMER_ID: (Optional but recommended)
   - Manager account ID (10 digits, no hyphens)
   - Used for accessing child accounts

Features Available:
- Generate keyword ideas from seed keywords or URLs
- Get historical metrics (search volume, competition, CPC)
- Find location and language IDs for targeting
- 12 months of monthly search volume data
- Competition index and bid estimates

Note: Keyword Planner API requires a Google Ads account with billing enabled.`
  },
  'github': {
    name: 'GitHub MCP',
    type: 'github',
    description: 'Access GitHub repositories, issues, and pull requests',
    command: 'npx',
    args: [
      '-y',
      '@modelcontextprotocol/server-github'
    ],
    env: {
      GITHUB_PERSONAL_ACCESS_TOKEN: ''
    },
    icon: 'Code'
  },
  'filesystem': {
    name: 'Filesystem MCP',
    type: 'filesystem',
    description: 'Read and write local files and directories',
    command: 'npx',
    args: [
      '-y',
      '@modelcontextprotocol/server-filesystem',
      '/path/to/allowed/directory'
    ],
    env: {},
    icon: 'FolderOpen'
  },
  'postgres': {
    name: 'PostgreSQL MCP',
    type: 'postgres',
    description: 'Query and manage PostgreSQL databases',
    command: 'npx',
    args: [
      '-y',
      '@modelcontextprotocol/server-postgres'
    ],
    env: {
      POSTGRES_CONNECTION_STRING: ''
    },
    icon: 'Database'
  },
  'slack': {
    name: 'Slack MCP',
    type: 'slack',
    description: 'Send messages and interact with Slack workspaces',
    command: 'npx',
    args: [
      '-y',
      '@modelcontextprotocol/server-slack'
    ],
    env: {
      SLACK_BOT_TOKEN: '',
      SLACK_TEAM_ID: ''
    },
    icon: 'MessageSquare'
  },
  'dataforseo': {
    name: 'DataForSEO MCP',
    type: 'dataforseo',
    description: 'Access DataForSEO APIs for SEO research, SERP data, keywords, backlinks, and more',
    command: 'npx',
    args: [
      '-y',
      'dataforseo-mcp-server'
    ],
    env: {
      DATAFORSEO_USERNAME: '',
      DATAFORSEO_PASSWORD: '',
      ENABLED_MODULES: 'SERP,KEYWORDS_DATA,ONPAGE,DATAFORSEO_LABS,BACKLINKS,BUSINESS_DATA,DOMAIN_ANALYTICS,CONTENT_ANALYSIS,AI_OPTIMIZATION',
      DATAFORSEO_FULL_RESPONSE: 'false',
      DATAFORSEO_SIMPLE_FILTER: 'false'
    },
    icon: 'Globe',
    helpText: `DataForSEO MCP Configuration:

⚠️ CRITICAL - Getting Your API Credentials:
===================================================

DO NOT use your DataForSEO account login password!

You need SPECIAL API CREDENTIALS:

1. Sign up or log in at: https://app.dataforseo.com/
2. Go to API Access page: https://app.dataforseo.com/api-access
3. Find your credentials:
   - API LOGIN: Usually your email address
   - API PASSWORD: Auto-generated password (DIFFERENT from your account password!)

⚠️ Important Notes:
- API password is shown for first 24 hours after registration
- After 24h, click "Send by email" to get your API password
- API password ≠ Account password (they are different!)
- Use Basic Authentication (handled automatically by this MCP)

Required Environment Variables:
===================================================
1. DATAFORSEO_USERNAME: Your API LOGIN (usually your email)
2. DATAFORSEO_PASSWORD: Your API PASSWORD (NOT your account password!)

Optional Modules (comma-separated):
===================================================
- SERP: Real-time search engine results (Google, Bing, Yahoo)
- KEYWORDS_DATA: Keyword research and search volume data
- ONPAGE: Website crawling and on-page SEO metrics
- DATAFORSEO_LABS: Keywords, SERPs, and domain analytics
- BACKLINKS: Backlink analysis and referring domains
- BUSINESS_DATA: Business reviews and information
- DOMAIN_ANALYTICS: Website traffic and technology data
- CONTENT_ANALYSIS: Brand monitoring and sentiment analysis
- AI_OPTIMIZATION: Keyword discovery and LLM optimization

Advanced Options:
===================================================
- DATAFORSEO_FULL_RESPONSE: Set to "true" for full API responses
- DATAFORSEO_SIMPLE_FILTER: Set to "true" for simplified filter schema (required for some LLMs)
- ENABLED_PROMPTS: Comma-separated list of specific prompts to enable

Example ENABLED_MODULES:
"SERP,KEYWORDS_DATA,BACKLINKS" - Only enable specific modules

Troubleshooting 401 Unauthorized:
===================================================
✗ Using account password instead of API password
✗ API password expired (request new one via email)
✗ Incorrect API login (check https://app.dataforseo.com/api-access)
✓ Use API password from API Access page
✓ API login is usually your email address
✓ Credentials are Base64 encoded automatically`
  },
  'custom': {
    name: 'Custom MCP',
    type: 'custom',
    description: 'Configure a custom MCP server',
    command: '',
    args: [],
    env: {},
    icon: 'Settings'
  }
};
